/**
 * LookupMachineSettings.tsx — the computer at the shop that holds the vendor session.
 *
 * The shop pays for ONE seat. Lookups used to open a browser and sign in again every
 * time, and the vendor's edge eventually refused the shop outright. The cure is one
 * window on a machine at the shop that stays open and stays signed in, with a small
 * service beside it that this server sends work to.
 *
 * This screen does the two things a person needs: say whether that machine is there
 * right now, and hand out the code that pairs a new one.
 *
 * What it deliberately does NOT do is claim to start anything. The server cannot
 * reach into the shop — the machine dials out, never the other way — so "launching"
 * it is three commands typed once on that computer, and they are printed here with
 * this server's own address already in them.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Empty,
  List,
  Modal,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Switch,
  Tooltip,
  Typography,
  App,
} from 'antd';
import {
  DesktopOutlined,
  PlusOutlined,
  ReloadOutlined,
  CopyOutlined,
  DisconnectOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import {
  repairDataAgentApi,
  type LookupMachine,
  type MachineState,
  type MachineSummary,
  type PairingCode,
} from '../../services/api/repairDataAgentApi';
import api from '../../services/api';

const { Title, Text, Paragraph } = Typography;

/**
 * One line per state, in the words someone would use to describe the problem.
 *
 * Every state names what to DO. "Offline" with no next step is the same unhelpful
 * shrug as "lookup failed", which is what this whole feature exists to stop saying.
 */
const STATES: Record<MachineState, { color: string; label: string; meaning: string }> = {
  ready: {
    color: 'green',
    label: 'Ready',
    meaning: 'Signed in and waiting. A lookup would run now.',
  },
  busy: {
    color: 'blue',
    label: 'Running a lookup',
    meaning: 'One job at a time — the shop has one seat. The next lookup waits its turn.',
  },
  needs_login: {
    color: 'orange',
    label: 'Needs signing in',
    meaning: 'The browser is open but the session expired. Sign in on that computer.',
  },
  chrome_down: {
    color: 'orange',
    label: 'Browser closed',
    meaning: 'The service is running but its browser is gone. Start the browser again.',
  },
  offline: {
    color: 'default',
    label: 'Offline',
    meaning: 'No heartbeat. The computer is off, asleep, or off the network.',
  },
  blocked_by_vendor: {
    color: 'red',
    label: 'Refused by the vendor',
    meaning: 'Lookups are paused for a short while rather than retried into a refusal.',
  },
  revoked: {
    color: 'red',
    label: 'Revoked',
    meaning: 'This machine’s token was taken away. Pair it again to use it.',
  },
};

const StateTag: React.FC<{ state: MachineState }> = ({ state }) => {
  const shown = STATES[state] ?? STATES.offline;
  return (
    <Tooltip title={shown.meaning}>
      <Tag color={shown.color} style={{ marginInlineEnd: 0 }}>
        {shown.label}
      </Tag>
    </Tooltip>
  );
};

function whenSeen(at: string | null): string {
  if (!at) return 'never checked in';
  const seen = new Date(at.includes('T') ? at : `${at.replace(' ', 'T')}Z`);
  if (Number.isNaN(seen.getTime())) return at;
  const seconds = Math.max(0, Math.round((Date.now() - seen.getTime()) / 1000));
  if (seconds < 90) return 'just now';
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} hr ago`;
  return seen.toLocaleString();
}

const LookupMachineSettings: React.FC = () => {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [machines, setMachines] = useState<LookupMachine[]>([]);
  const [summary, setSummary] = useState<MachineSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pairing, setPairing] = useState<PairingCode | null>(null);
  const [rotated, setRotated] = useState<{ name: string; token: string } | null>(null);

  // The switch that decides whether lookups run at all lives on the AI settings
  // card further up the page. Pairing a machine here and then finding lookups
  // still refused is a dead end with no signpost — it happened — so the same
  // toggle is offered next to the machine it controls. Both read and write the
  // one field; there is no second source of truth.
  const [lookupEnabled, setLookupEnabled] = useState<boolean | null>(null);
  const [aiEnabled, setAiEnabled] = useState<boolean>(true);
  const [togglingLookup, setTogglingLookup] = useState(false);

  const loadFeatureState = useCallback(async () => {
    try {
      const { data } = await api.get('/ai-settings');
      setLookupEnabled(!!data?.ai_lookup_enabled);
      setAiEnabled(!!data?.ai_enabled);
    } catch {
      // A settings read that fails should not take the pairing panel down with it.
      setLookupEnabled(null);
    }
  }, []);

  useEffect(() => { void loadFeatureState(); }, [loadFeatureState]);

  const toggleLookup = async (next: boolean) => {
    setTogglingLookup(true);
    try {
      await api.put('/ai-settings', { ai_lookup_enabled: next });
      setLookupEnabled(next);
      message.success(next ? 'Lookups are on.' : 'Lookups are off.');
    } catch {
      message.error('Could not change that setting.');
    } finally {
      setTogglingLookup(false);
    }
  };

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const data = await repairDataAgentApi.list();
      setMachines(data.agents ?? []);
      setSummary(data.machine ?? null);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? 'Could not read the lookup machines.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    // Slow on purpose. This is a status line, not a live console, and the state it
    // shows changes on the order of minutes.
    const timer = setInterval(() => void load(true), 15000);
    return () => clearInterval(timer);
  }, [load]);

  const copy = async (value: string, what: string) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success(`${what} copied`);
    } catch {
      // Clipboard access is denied often enough on mobile that a failure has to be
      // survivable: the value is on screen and can be typed.
      message.warning(`Could not copy — the ${what.toLowerCase()} is on screen`);
    }
  };

  const pair = async () => {
    setBusy(true);
    try {
      setPairing(await repairDataAgentApi.createPairingCode());
    } catch (err: any) {
      message.error(err?.response?.data?.error?.message ?? 'Could not create a pairing code.');
    } finally {
      setBusy(false);
    }
  };

  const rotate = async (machine: LookupMachine) => {
    setBusy(true);
    try {
      const { token } = await repairDataAgentApi.rotate(machine.id);
      setRotated({ name: machine.name, token });
      await load(true);
    } catch (err: any) {
      message.error(err?.response?.data?.error?.message ?? 'Could not issue a new token.');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (machine: LookupMachine) => {
    setBusy(true);
    try {
      await repairDataAgentApi.revoke(machine.id);
      message.success(`${machine.name} can no longer connect.`);
      await load(true);
    } catch (err: any) {
      message.error(err?.response?.data?.error?.message ?? 'Could not revoke that machine.');
    } finally {
      setBusy(false);
    }
  };

  const serverUrl = apiOrigin();
  const live = summary?.state ?? 'offline';

  return (
    <Card
      title={
        <Space>
          <DesktopOutlined />
          <span>Lookup machine</span>
          <StateTag state={live} />
        </Space>
      }
      extra={
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => void load()}
            aria-label="Refresh"
            size="small"
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={pair} loading={busy} size="small">
            Pair a machine
          </Button>
        </Space>
      }
    >
      {lookupEnabled !== null && (
        <div
          data-testid="lookup-feature-toggle-row"
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            gap: 12, marginBottom: 16, padding: '10px 12px',
            background: '#fafafa', borderRadius: 8,
          }}
        >
          <div>
            <Text strong>Procedure lookups</Text>
            <Paragraph type="secondary" style={{ margin: 0, fontSize: 12 }}>
              {aiEnabled
                ? 'Off means the app refuses a lookup even with a machine paired.'
                : 'AI features are off for this shop, so this stays off too.'}
            </Paragraph>
          </div>
          <Switch
            data-testid="lookup-feature-toggle"
            checked={!!lookupEnabled}
            disabled={!aiEnabled}
            loading={togglingLookup}
            onChange={(v) => void toggleLookup(v)}
            checkedChildren="On"
            unCheckedChildren="Off"
          />
        </div>
      )}

      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Lookups run in a browser on a computer at the shop, signed into the vendor once
        and left open. The shop has one seat, so one machine holds it and one lookup
        runs at a time.
      </Paragraph>

      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}

      {live !== 'ready' && live !== 'busy' && !loading && (
        <Alert
          type={live === 'offline' ? 'info' : 'warning'}
          showIcon
          style={{ marginBottom: 16 }}
          message={STATES[live].label}
          description={
            <>
              {STATES[live].meaning}
              {' '}
              Lookups started now will wait rather than fail.
            </>
          }
        />
      )}

      {loading ? (
        <Spin />
      ) : error ? null : machines.length === 0 ? (
        /* Only when the list was actually READ. An unreadable list and an empty one
           are different facts, and showing "nothing paired" for a failed request
           sends someone off to pair a machine they already have. */
        <Empty
          description="No machine paired yet. Pair one to run lookups from the app."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <List
          itemLayout="vertical"
          dataSource={machines}
          renderItem={(machine) => (
            <List.Item
              key={machine.id}
              actions={[
                <Popconfirm
                  key="rotate"
                  title="Issue a new token?"
                  description="The current token stops working immediately, and that machine must be set up with the new one."
                  onConfirm={() => void rotate(machine)}
                  okText="Issue"
                >
                  <Button size="small" icon={<KeyOutlined />} disabled={busy}>
                    New token
                  </Button>
                </Popconfirm>,
                <Popconfirm
                  key="revoke"
                  title="Revoke this machine?"
                  description="It disconnects at once and cannot come back without being paired again."
                  onConfirm={() => void revoke(machine)}
                  okText="Revoke"
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    size="small"
                    danger
                    icon={<DisconnectOutlined />}
                    disabled={busy || !!machine.revokedAt}
                  >
                    Revoke
                  </Button>
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                title={
                  <Space wrap>
                    <Text strong>{machine.name}</Text>
                    <StateTag state={machine.state} />
                  </Space>
                }
                description={
                  <Space direction="vertical" size={0}>
                    <Text type="secondary">Last checked in {whenSeen(machine.lastSeenAt)}</Text>
                    <Text type="secondary">
                      {machine.lastIp ? `Seen from ${machine.lastIp}` : 'No address recorded yet'}
                      {machine.agentBuild ? ` · build ${machine.agentBuild}` : ''}
                    </Text>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      )}

      {/* Pairing: the code is shown ONCE. */}
      <Modal
        open={!!pairing}
        onCancel={() => setPairing(null)}
        title="Set up the shop’s lookup machine"
        footer={<Button onClick={() => setPairing(null)}>Done</Button>}
        width={640}
        /* The phone is the primary screen here — the owner pairs the machine standing
           next to it.
           A max-width alone was NOT enough, which a screenshot at 390 showed and the
           earlier assertion had missed: the dialog box shrank obediently while its
           contents stayed laid out at 640 and were cut off at the edge — the code's
           Copy button gone entirely, the --server line sliced in half. The contents
           have to be allowed to shrink too, which is what `minWidth: 0` below does. */
        style={{ maxWidth: '94vw' }}
      >
        {pairing && (
          <Space direction="vertical" size="middle" style={{ width: '100%', minWidth: 0 }}>
            <Alert
              type="warning"
              showIcon
              message="This code is shown once and works once."
              description={`It stops working at ${new Date(pairing.expiresAt).toLocaleTimeString()}. Generate another if it expires.`}
            />

            <Card size="small">
              {/* Wraps on a narrow screen. Unwrapped, the Copy button sat off the
                  right edge of a phone with the code itself only half visible. */}
              <Space
                align="center"
                wrap
                style={{ justifyContent: 'space-between', width: '100%' }}
              >
                <Title level={3} style={{ margin: 0, letterSpacing: 2 }}>
                  {pairing.code}
                </Title>
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => void copy(pairing.code, 'Pairing code')}
                >
                  Copy
                </Button>
              </Space>
            </Card>

            <div style={{ minWidth: 0 }}>
              <Text strong>On the shop computer, once:</Text>
              <ol style={{ paddingInlineStart: 20, marginBottom: 8 }}>
                <li>
                  Replace <Text code>{INSTALL_PATH_PLACEHOLDER}</Text> with wherever the
                  CNF folder actually is on that machine.
                </li>
                <li>
                  Run the first two lines. A browser window opens — sign in to the vendor
                  there, by hand, this once. Leave it open.
                </li>
                <li>Run the last two lines in a second terminal, in the same folder.</li>
              </ol>
              <pre
                style={{
                  background: 'rgba(127,127,127,0.12)',
                  padding: 12,
                  borderRadius: 6,
                  margin: 0,
                  // WRAPPED, not scrolled. A pre with long unbreakable lines sized the
                  // whole dialog to its widest command — 586px inside a 342px body —
                  // and everything beside it stretched to match and was cut off at the
                  // screen edge. Wrapping removes the pressure entirely, and Copy
                  // commands still copies the exact text, so nothing depends on the
                  // line breaks a phone happens to choose.
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxWidth: '100%',
                }}
              >
                {setupCommands(serverUrl, pairing.code)}
              </pre>
              <Button
                icon={<CopyOutlined />}
                style={{ marginTop: 8 }}
                onClick={() => void copy(setupCommands(serverUrl, pairing.code), 'Commands')}
              >
                Copy commands
              </Button>
            </div>

            <Text type="secondary">
              The machine dials out to this server; nothing connects to the shop, so no
              router change is needed. Leave the browser window open and signed in.
            </Text>
          </Space>
        )}
      </Modal>

      {/* A rotated token is the same one-time secret as a pairing code. */}
      <Modal
        open={!!rotated}
        onCancel={() => setRotated(null)}
        title={`New token for ${rotated?.name ?? ''}`}
        style={{ maxWidth: '96vw' }}
        footer={<Button onClick={() => setRotated(null)}>Done</Button>}
      >
        {rotated && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Alert
              type="warning"
              showIcon
              message="Shown once. The old token has already stopped working."
            />
            <Text code style={{ wordBreak: 'break-all' }}>
              {rotated.token}
            </Text>
            <Button icon={<CopyOutlined />} onClick={() => void copy(rotated.token, 'Token')}>
              Copy token
            </Button>
            <Text type="secondary">
              Put it in the machine’s config file as <Text code>token</Text>, then start the
              service again.
            </Text>
          </Space>
        )}
      </Modal>
    </Card>
  );
};

/**
 * Where the CNF folder sits on the shop machine.
 *
 * A placeholder, and it has to be: this server has no way to know where someone
 * installed the app on a computer it cannot see, and a guessed path is a command that
 * fails in a way that looks like the feature is broken.
 */
export const INSTALL_PATH_PLACEHOLDER = 'C:\\path\\to\\CNF Auto App';

/**
 * The address the MACHINE must dial, which is the API's origin — not the page's.
 *
 * In production they are the same host and the difference never shows. In local
 * development the app is served on one port and the API listens on another, and the
 * agent holds a WebSocket rather than an ordinary request: it cannot ride the dev
 * server's proxy the way a fetch can. Printing the page's origin there produces a
 * machine that pairs and then never connects, which is the least debuggable outcome
 * available.
 *
 * `REACT_APP_API_URL` is `<origin>/api` by this app's convention, so the origin is
 * what is left after that suffix.
 */
export function apiOrigin(): string {
  const configured = process.env.REACT_APP_API_URL;
  if (!configured) return window.location.origin;
  try {
    return new URL(configured, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
}

/**
 * The commands, in order, with this server's address already in them.
 *
 * `npm run repair-data:chrome` rather than a raw `chrome.exe`: Chrome is not on the
 * PATH on a normal Windows box, and — the part that actually bites — running
 * chrome.exe with a debugging flag while Chrome is ALREADY open just adds a tab to
 * the existing process and opens no port at all. The script finds the executable,
 * gives that window its own profile so the signed-in session is not shared with
 * whatever else the browser is doing, and refuses to start a second one.
 *
 * The `cd` is first because npm run only works inside the project, which is the other
 * way this goes wrong: run from a home directory it reports a missing package.json,
 * which reads like a broken install rather than a wrong folder.
 */
export function setupCommands(serverUrl: string, code: string, installPath?: string): string {
  return [
    `cd "${installPath || INSTALL_PATH_PLACEHOLDER}\\backend"`,
    'npm run repair-data:chrome',
    `npm run agent:pair -- --server ${serverUrl} --code ${code}`,
    'npm run agent',
  ].join('\n');
}

export default LookupMachineSettings;
